import React, { useState } from "react";
import {
  List,
  ListItem,
  ListItemText,
  Drawer,
  Box,
  Collapse,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  ChevronRight,
  Dashboard,
  People,
  LocalOffer,
  AddCircle,
  Badge,
  Description,
} from "@mui/icons-material";
import AssignmentReturnedIcon from "@mui/icons-material/AssignmentReturned";
import PostAddIcon from "@mui/icons-material/PostAdd";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import MailIcon from "@mui/icons-material/Mail";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import ImageIcon from "@mui/icons-material/Image";
import ApprovalIcon from "@mui/icons-material/Approval";
import GetAppIcon from "@mui/icons-material/GetApp";
import { Link } from "react-router-dom";
import PermissionGuard from "./PermissionGuard";

const Sidebar = ({ userRole, showSidebar, toggleSidebar }) => {
  const [open, setOpen] = useState({});
  const [collapsed, setCollapsed] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const handleToggle = (key) => {
    setOpen((prevOpen) => {
      // If the clicked menu is already open, just close it
      if (prevOpen[key]) {
        return {
          ...prevOpen,
          [key]: false,
        };
      }

      // Otherwise, close all other menus and open the clicked one
      const newOpen = {};
      Object.keys(prevOpen).forEach(k => {
        newOpen[k] = false;
      });
      newOpen[key] = true;

      return newOpen;
    });
  };

  const handleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const drawerWidth = collapsed ? (isMobile ? 60 : 240) : isMobile ? 240 : 240;

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"} // 'temporary' for mobile, 'permanent' for larger screens
      open={showSidebar}
      onClose={toggleSidebar}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          padding: "16px",
          transition: "width 0.3s",
        },
      }}
    >
      <Box
        sx={{ display: "flex", justifyContent: "flex-end", padding: "0 8px" }}
      >
        <IconButton onClick={handleCollapse}>
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>
      <Box sx={{ overflow: "auto" }}>
        <List>
          {/* Dashboard - Visible to all authenticated users */}
          <ListItem button onClick={() => handleToggle("dashboard")}>
            <Dashboard sx={{ mr: 2, fontSize: 24 }} />
            <ListItemText primary="Dashboard" />
            {open["dashboard"] ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={open["dashboard"]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem
                button
                component={Link}
                to="/dashboard/home"
                sx={{ pl: 4 }}
              >
                <Dashboard sx={{ mr: 2, fontSize: 24 }} />
                <ListItemText primary="Home" />
              </ListItem>
              <ListItem
                button
                component={Link}
                to="/dashboard/settings"
                sx={{ pl: 4 }}
              >
                <Dashboard sx={{ mr: 2, fontSize: 24 }} />
                <ListItemText primary="Settings" />
              </ListItem>
            </List>
          </Collapse>

          {/* User Management */}
          <PermissionGuard
            anyOf={[
              { resource: 'user', action: 'view' },
              { resource: 'role', action: 'view' }
            ]}
          >
            <ListItem button onClick={() => handleToggle("userManagement")}>
              <People sx={{ mr: 2, fontSize: 24 }} />
              <ListItemText primary="User Management" />
              {open["userManagement"] ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse
              in={open["userManagement"]}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <PermissionGuard resource="user" action="create">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/register"
                    sx={{ pl: 4 }}
                  >
                    <AddCircle sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="Register" />
                  </ListItem>
                </PermissionGuard>
                <PermissionGuard resource="user" action="view">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/users"
                    sx={{ pl: 4 }}
                  >
                    <People sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="Users Accounts" />
                  </ListItem>
                </PermissionGuard>
                <PermissionGuard resource="role" action="view">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/roles"
                    sx={{ pl: 4 }}
                  >
                    <People sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="User Role" />
                  </ListItem>
                </PermissionGuard>
                <PermissionGuard resource="role" action="edit">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/role-permissions"
                    sx={{ pl: 4 }}
                  >
                    <People sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="Role Permissions" />
                  </ListItem>
                </PermissionGuard>
              </List>
            </Collapse>
          </PermissionGuard>

          {/* Employee Management */}
          <PermissionGuard resource="employee" action="view">
            <ListItem button onClick={() => handleToggle("employeeManagement")}>
              <Badge sx={{ mr: 2, fontSize: 24 }} />
              <ListItemText primary="Employee Management" />
              {open["employeeManagement"] ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse
              in={open["employeeManagement"]}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <PermissionGuard resource="employee" action="create">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/employees/add"
                    sx={{ pl: 4 }}
                  >
                    <AddCircle sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="Add Employee" />
                  </ListItem>
                </PermissionGuard>
                <PermissionGuard resource="employee" action="view">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/employees"
                    sx={{ pl: 4 }}
                  >
                    <Badge sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="All Employees" />
                  </ListItem>
                </PermissionGuard>
                <PermissionGuard resource="employment_letter" action="view">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/employment-letter-template"
                    sx={{ pl: 4 }}
                  >
                    <Description sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="Letter Template" />
                  </ListItem>
                </PermissionGuard>
              </List>
            </Collapse>
          </PermissionGuard>

          <PermissionGuard resource="bookmark" action="view">
            <ListItem
              button
              onClick={() => handleToggle("bookmarkManagement")}
            >
              <AssignmentReturnedIcon sx={{ mr: 2, fontSize: 24 }} />
              <ListItemText primary="Bookmark Management" />
              {open["bookmarkManagement"] ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse
              in={open["bookmarkManagement"]}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <PermissionGuard resource="bookmark" action="create">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/addBookmark"
                    sx={{ pl: 4 }}
                  >
                    <AddCircle sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="Add Bookmark" />
                  </ListItem>
                </PermissionGuard>
                <PermissionGuard resource="bookmark" action="view">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/bookmarks"
                    sx={{ pl: 4 }}
                  >
                    <LocalOffer sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="Bookmarks" />
                  </ListItem>
                </PermissionGuard>
              </List>
            </Collapse>
          </PermissionGuard>

          <PermissionGuard resource="approval" action="view">
            <ListItem
              button
              onClick={() => handleToggle("approvalManagement")}
            >
              <ApprovalIcon sx={{ mr: 2, fontSize: 24 }} />
              <ListItemText primary="Approval Workflow" />
              {open["approvalManagement"] ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse
              in={open["approvalManagement"]}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <PermissionGuard resource="approval" action="view">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/approvals"
                    sx={{ pl: 4 }}
                  >
                    <ApprovalIcon sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="Pending Approvals" />
                  </ListItem>
                </PermissionGuard>
              </List>
            </Collapse>
          </PermissionGuard>

          <PermissionGuard
            anyOf={[
              { resource: 'blog', action: 'view' },
              { resource: 'blog', action: 'create' }
            ]}
          >
            <ListItem button onClick={() => handleToggle("blogManagement")}>
              <PostAddIcon sx={{ mr: 2, fontSize: 24 }} />
              <ListItemText primary="Blog Management" />
              {open["blogManagement"] ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse
              in={open["blogManagement"]}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <PermissionGuard resource="blog" action="create">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/addBlog"
                    sx={{ pl: 4 }}
                  >
                    <AddCircle sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="Add Blog" />
                  </ListItem>
                </PermissionGuard>
                <PermissionGuard
                  anyOf={[
                    { resource: 'blog', action: 'view' },
                    { resource: 'blog', action: 'edit' }
                  ]}
                >
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/blogs"
                    sx={{ pl: 4 }}
                  >
                    <LocalOffer sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="Blogs" />
                  </ListItem>
                </PermissionGuard>
              </List>
            </Collapse>
          </PermissionGuard>

          <PermissionGuard resource="gallery" action="view">
            <ListItem button onClick={() => handleToggle("galleryManagement")}>
              <PhotoLibraryIcon sx={{ mr: 2, fontSize: 24 }} />
              <ListItemText primary="Gallery Management" />
              {open["galleryManagement"] ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse
              in={open["galleryManagement"]}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <PermissionGuard resource="gallery" action="view">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/galleryManagement"
                    sx={{ pl: 4 }}
                  >
                    <PhotoLibraryIcon sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="View All" />
                  </ListItem>
                </PermissionGuard>
                <PermissionGuard resource="gallery" action="create">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/addGallery"
                    sx={{ pl: 4 }}
                  >
                    <AddCircle sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="Add Gallery" />
                  </ListItem>
                </PermissionGuard>
                <PermissionGuard resource="gallery" action="view">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/galleries"
                    sx={{ pl: 4 }}
                  >
                    <PhotoLibraryIcon sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="Galleries" />
                  </ListItem>
                </PermissionGuard>
                <PermissionGuard resource="app_download" action="view">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/app-download-settings"
                    sx={{ pl: 4 }}
                  >
                    <GetAppIcon sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="App Download Settings" />
                  </ListItem>
                </PermissionGuard>
              </List>
            </Collapse>
          </PermissionGuard>

          <PermissionGuard resource="tracker" action="view">
            <ListItem
              button
              onClick={() => handleToggle("trackerManagement")}
            >
              <GpsFixedIcon sx={{ mr: 2, fontSize: 24 }} />
              <ListItemText primary="Tracker Management" />
              {open["trackerManagement"] ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse
              in={open["trackerManagement"]}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <PermissionGuard resource="tracker" action="view">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/trackers"
                    sx={{ pl: 4 }}
                  >
                    <GpsFixedIcon sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="GPS Trackers" />
                  </ListItem>
                </PermissionGuard>
              </List>
            </Collapse>
          </PermissionGuard>

          <PermissionGuard resource="hero_image" action="view">
            <ListItem button onClick={() => handleToggle("heroImageManagement")}>
              <ImageIcon sx={{ mr: 2, fontSize: 24 }} />
              <ListItemText primary="Hero Image Management" />
              {open["heroImageManagement"] ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse
              in={open["heroImageManagement"]}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <PermissionGuard resource="hero_image" action="view">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/hero-images"
                    sx={{ pl: 4 }}
                  >
                    <ImageIcon sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="Manage Hero Images" />
                  </ListItem>
                </PermissionGuard>
              </List>
            </Collapse>
          </PermissionGuard>

          <PermissionGuard resource="contact_message" action="view">
            <ListItem button onClick={() => handleToggle("contactManagement")}>
              <MailIcon sx={{ mr: 2, fontSize: 24 }} />
              <ListItemText primary="Contact Messages" />
              {open["contactManagement"] ? <ExpandLess /> : <ExpandMore />}
            </ListItem>
            <Collapse
              in={open["contactManagement"]}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <PermissionGuard resource="contact_message" action="view">
                  <ListItem
                    button
                    component={Link}
                    to="/dashboard/contact/messages"
                    sx={{ pl: 4 }}
                  >
                    <MailIcon sx={{ mr: 2, fontSize: 24 }} />
                    <ListItemText primary="View Messages" />
                  </ListItem>
                </PermissionGuard>
              </List>
            </Collapse>
          </PermissionGuard>
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
